import { handleActions } from 'redux-actions';
import {
  HotelContentSchema,
  RoomSchema,
  RateSchema,
} from '@airasia-common/libraries/validation/schemas';
import {
  AmenityIconsMap,
  SORT_TYPE,
  ROOMS_LEFT_TAG,
  CHECKOUT_STATUS_CODE,
  AmenityRanking,
  CANCELLATION_REFUND_TYPE,
} from '@airasia-common/libraries/constants';
import { CHARGE_TYPE } from '@airasia-common/libraries/constants/Payment';
import {
  getAmenityTranslationKey,
  AMENITY_ICON_TYPE,
} from '@airasia-common/libraries/utilities/AmenityHelper';
import {
  DISPLAY_NAME,
  reduceAggregations,
  getGuestRatingLabelId,
} from '../AggregationHelper';
import {
  HERO_SIZE,
  THUMBNAIL_SIZE,
  ENABLE_SORTING_HOTEL_LIST_AT_CLIENT,
} from '../../constants';
import {
  getHeroImage,
  mapHotelAttributes,
  makeGalleryFromImages,
  getAgeRange,
  makeGalleryV2FromImages,
  makeImageCarouselFromImages,
} from '../../helpers';
import {
  filterPenalties,
  getRefundType,
  isActivePenalty,
} from '../booking/cancellation';
import { types } from './hotel.meta';

const initialAggregationMap = {
  [DISPLAY_NAME.GUEST_RATING]: [{ id: '4.5' }, { id: '4.0' }, { id: '3.0' }],
};

const PENALTY_TYPE_FULL = 'Full';
const RECOMMEND_KEY = 'recommended';
const RESOURCE_NOT_FOUND = 'resource_not_found';
const STAFF_KEY = 'staff_travel_rate';

const roundOffPrice = price => {
  let numConvert = price;
  if (typeof price === 'string') {
    numConvert = parseFloat(price);
  }
  if (!numConvert) return false;

  return Math.floor(numConvert).toString();
};

const initialState = {
  aggregationMap: initialAggregationMap,
  hotelList: [],
  stayDetail: {
    checkin_date: '',
    checkout_date: '',
  },
  refreshToken: '',
  isFetching: false,
  isFailed: false,
  pageNumber: 0,
  flyerDiscount: false,
  hasFetchedAll: false,
  hotelDetail: {
    isFetching: false,
    isFailed: false,
  },
  checkout: {
    paymentUrl: '',
    isFetching: false,
    isFailed: false,
    isRedirecting: false,
    priceChanged: false,
    hotelSoldOut: false,
    bookingRequestId: '',
    successFullOrder: false,
  },
  specialRequests: [],
  defaultLocaleUsed: false,
  guestReviews: {
    isFetching: false,
    isFailed: false,
  },
  preCheckout: {
    isFailed: false,
    isFetching: false,
    error: null,
    extraNote: '',
    data: null,
    priceChanged: false,
    roomUnavailable: false,
    newRoomRate: null,
    errorMessage: '',
  },
};

export function fetchHotelList(state) {
  return {
    ...state,
    isFetching: true,
  };
}

export function fetchHotelListV2(state) {
  return {
    ...state,
    isFetching: true,
  };
}

const orderingFunctions = {
  [SORT_TYPE.RECOMMENDED]: (a, b) => a['recommended'] - b['recommended'],
  [SORT_TYPE.PRICE_LOW_TO_HIGH]: (a, b) =>
    Boolean(a?.baseRate?.value && b?.baseRate?.value) &&
    parseFloat(a.baseRate.value) - parseFloat(b.baseRate.value),
  [SORT_TYPE.PRICE_HIGH_TO_LOW]: (a, b) =>
    Boolean(a?.baseRate?.value && b?.baseRate?.value) &&
    parseFloat(b.baseRate.value) - parseFloat(a.baseRate.value),
  [SORT_TYPE.USER_RATING]: (a, b) =>
    a.guestRating && b.guestRating
      ? parseFloat(b.guestRating) - parseFloat(a.guestRating)
      : 0,
};

const applyOrdering = (hotels, orderBy) => {
  if (!orderingFunctions[orderBy]) {
    throw new Error(`ordering not supported ${orderBy}`);
  }

  return hotels.sort(orderingFunctions[orderBy]);
};

export const toHotelListWithFocus = (focusId, hotelList) => {
  if (focusId && typeof focusId === 'string') {
    const focusList = hotelList.filter(hotel => hotel.id === focusId);
    const unfocusList = hotelList.filter(hotel => hotel.id !== focusId);

    return [...focusList, ...unfocusList];
  }
  return hotelList;
};

export function sortHotelList(state, action) {
  const { hotelList } = state;
  const { sortBy } = action.payload;
  const orderedList = applyOrdering(hotelList, sortBy);
  return {
    ...state,
    hotelList: orderedList.map(i => {
      if (i.focusHighLight) {
        i.highlight = false;
        i.focusHighLight = false;
      }

      return i;
    }),
  };
}

export function fetchHotelListCompleted(state, action) {
  const { payload } = action;
  const {
    aggregations,
    hotels,
    sortBy,
    pageNumber = 0,
    flyerDiscount,
    defaultLocaleUsed,
    bigMembership,
    stay_detail,
  } = payload;
  const hasFetchedAll = !hotels || hotels.length === 0;

  const newAggregationMap = reduceAggregations(aggregations);
  const aggregationMap =
    Object.values(newAggregationMap).length > 0
      ? newAggregationMap
      : state.aggregationMap;

  let hotelList = reduceHotels(aggregationMap, hotels);
  if (
    sortBy &&
    sortBy !== SORT_TYPE.RECOMMENDED &&
    ENABLE_SORTING_HOTEL_LIST_AT_CLIENT
  ) {
    hotelList = applyOrdering(hotelList, sortBy);
  }

  hotelList = toHotelListWithFocus(payload.focusId, hotelList);

  let pageNum = hasFetchedAll ? state.pageNumber : state.pageNumber + 1;
  pageNum = pageNumber + 1 > pageNum ? pageNumber + 1 : pageNum;
  return {
    ...state,
    hasFetchedAll,
    aggregationMap,
    hotelList: hasFetchedAll
      ? state.hotelList
      : [...state.hotelList, ...hotelList],
    isFetching: false,
    pageNumber: pageNum,
    flyerDiscount,
    defaultLocaleUsed,
    bigMembership,
    stayDetail: stay_detail,
  };
}

const reduceHotels = (aggregationMap, hotels) => {
  const result = [];

  hotels.forEach((hotel, index) => {
    const {
      id,
      name,
      prices,
      gallery,
      star_rating,
      debug,
      guest_rating,
      headline,
      tags,
      is_hotel_selected: isHotelSelected,
      tripadvisor,
      location,
      amenities,
      distance_from_poi,
    } = hotel;
    const {
      id: discountId,
      type: discountType,
      description: discountDescription,
    } = prices?.rate?.discount || {};
    let {
      base_rate: baseRate,
      strike_through: strikethroughPrice,
      price_after_deducting_big_points: baseRateAfterBigPointsDeduction,
    } = prices?.rate?.lead_price || {};
    if (baseRate && baseRate.value) {
      baseRate.value = roundOffPrice(baseRate.value);
    }
    if (strikethroughPrice && strikethroughPrice.value) {
      strikethroughPrice.value = roundOffPrice(strikethroughPrice.value);
    }
    if (
      baseRateAfterBigPointsDeduction &&
      baseRateAfterBigPointsDeduction.value
    ) {
      baseRateAfterBigPointsDeduction.value = roundOffPrice(
        baseRateAfterBigPointsDeduction.value
      );
    }
    const { big_life: bigPoint } = prices?.rate || {};
    const { images } = gallery || {};
    const starRating =
      star_rating &&
      star_rating.rating &&
      star_rating.rating.length &&
      parseFloat(star_rating.rating);
    const {
      rating: guestRating,
      count: guestRatingCount,
      amenities: amenitiesRating,
      cleanliness: cleanlinessRating,
      comfort: comfortRating,
      condition: conditionRating,
      location: locationRating,
      neighbourhood: neighbourhoodRating,
      quality: qualityRating,
      service: serviceRating,
      value: valueRating,
    } = guest_rating || {};
    const guestRatingLabelId = getGuestRatingLabelId(
      aggregationMap,
      guestRating
    );
    const {
      label_above: labelAbove,
      left_view: leftView,
      right_view: rightView,
    } = tags || {};
    const aboveNameLabels = labelAbove && labelAbove.reduce(tagsReducer, []);
    const leftPillTexts = leftView && leftView.reduce(tagsReducer, []);
    const rightPillTexts = rightView && rightView.reduce(tagsReducer, []);

    const image = getHeroImage(images, THUMBNAIL_SIZE);
    const { type: paymentDiscountType } = prices?.rate.discount || {};
    const isCrossSellRate = prices?.rate?.cross_sell_rate;
    const imagesCarousel = makeImageCarouselFromImages(images, THUMBNAIL_SIZE);
    const {
      pay_at_hotel_available: isPayAtHotel,
      buy_now_pay_later_available: isPayLater,
    } = prices || {};

    result.push({
      id,
      name,
      baseRate,
      strikethroughPrice,
      image,
      starRating,
      debug,
      guestRating,
      guestRatingLabelId,
      guestRatingCount,
      amenitiesRating,
      cleanlinessRating,
      comfortRating,
      conditionRating,
      locationRating,
      neighbourhoodRating,
      qualityRating,
      serviceRating,
      valueRating,
      aboveNameLabels,
      leftPillTexts,
      rightPillTexts,
      discountId,
      discountType,
      discountDescription,
      distancePoi: distance_from_poi,
      tags,
      location: headline,
      recommended: index,
      bigPoint,
      paymentDiscountType,
      isPayAtHotel,
      isCrossSellRate,
      isHotelSelected,
      images: imagesCarousel,
      baseRateAfterBigPointsDeduction,
      tripadvisor,
      coordinates: location,
      isPayLater,
      amenities,
      prices,
    });
  });

  return result;
};

const getTagTranslation = ({ id, value = '', data = {} }) => {
  if (!id) {
    return '';
  }
  const { number_of_rooms: numberOfRooms } = data;
  if (numberOfRooms && numberOfRooms > 0) {
    return { translationId: `tags.${id}`, numberOfRooms, value };
  } else if (id !== ROOMS_LEFT_TAG) {
    return { translationId: `tags.${id}`, value };
  }
  return value ? { value } : '';
};

const tagsReducer = (result, tag) => {
  tag && result.push(getTagTranslation(tag));
  return result;
};

export function clearHotelList() {
  return initialState;
}

export function fetchHotelListFailed(state) {
  return {
    ...state,
    isFailed: true,
    isFetching: false,
  };
}

export function fetchHotelDetail(state) {
  return {
    ...state,
    hotelDetail: {
      isFailed: false,
      isFetching: true,
    },
  };
}

export function fetchHotelDetailFailed(state, action) {
  const { type } = action.payload;
  if (type === RESOURCE_NOT_FOUND) {
    return {
      ...state,
      hotelDetail: {
        isFailed: true,
        isFetching: false,
        isResourceNotFound: true,
      },
    };
  }
  return {
    ...state,
    hotelDetail: {
      isFailed: true,
      isFetching: false,
    },
  };
}

export function fetchHotelDetailCompleted(state, action) {
  const hotelDetail = { ...action.payload };

  try {
    HotelContentSchema.validateSync(hotelDetail);

    const {
      gallery,
      address,
      name,
      id,
      amenities,
      description,
      star_rating,
      guest_rating,
      checkin_details,
      checkout_details,
      policies,
      fees,
      attributes,
      default_locale_used,
      tripadvisor,
      location,
      nearbyPoi,
    } = action.payload;
    const displayAmenities =
      amenities &&
      amenities.top_amenities &&
      amenities.top_amenities
        .filter(({ name }) => Boolean(name))
        .map(amenity => {
          return {
            ...amenity,
            displayId: `hotel${AmenityIconsMap.split.level}${
              AmenityIconsMap.hotel[amenity.id]
            }`,
            translationKey: getAmenityTranslationKey(
              AMENITY_ICON_TYPE.HOTEL,
              amenity.id
            ),
            ranking: AmenityRanking.hotel[AmenityIconsMap.hotel[amenity.id]],
          };
        })
        .sort((a, b) => a.ranking - b.ranking);

    const topNotification = mapHotelAttributes(attributes);

    const allAmenities =
      amenities &&
      amenities.top_amenities &&
      amenities.top_amenities
        .filter(({ name }) => Boolean(name))
        .map(amenity => {
          return {
            ...amenity,
            displayId: `hotel${AmenityIconsMap.split.level}${
              AmenityIconsMap.hotel[amenity.id]
            }`,
            translationKey: getAmenityTranslationKey(
              AMENITY_ICON_TYPE.HOTEL,
              amenity.id
            ),
          };
        });

    const { images } = gallery || {};
    const image =
      getHeroImage(images, HERO_SIZE) || getHeroImage(images, THUMBNAIL_SIZE);
    const starRating =
      star_rating &&
      star_rating.rating &&
      star_rating.rating.length &&
      parseFloat(star_rating.rating);
    const {
      rating: guestRating,
      count: guestRatingCount,
      amenities: amenitiesRating,
      cleanliness: cleanlinessRating,
      comfort: comfortRating,
      condition: conditionRating,
      location: locationRating,
      neighbourhood: neighbourhoodRating,
      quality: qualityRating,
      service: serviceRating,
      value: valueRating,
    } = guest_rating || {};
    const guestRatingLabelId = getGuestRatingLabelId(
      state.aggregationMap,
      guestRating
    );

    const { age_policy } = policies;

    let childPolicies;

    if (age_policy) {
      const {
        children_age_from,
        children_age_to,
        min_adult_age,
        min_guest_age,
      } = age_policy;
      childPolicies = [
        getAgeRange(min_guest_age, children_age_from),
        getAgeRange(children_age_from, children_age_to),
        getAgeRange(min_adult_age, null),
      ].map(range => {
        if (range.minAge === 0 && range.maxAge === 0) return null;
        return range;
      });
    }

    let pointsOfInterest = null;
    if (nearbyPoi && Array.isArray(nearbyPoi) && nearbyPoi.length > 1) {
      pointsOfInterest = nearbyPoi.map(poi => {
        const { distance } = poi;
        const kmsDistance = distance.slice(0, distance.indexOf('/') - 1);
        return {
          ...poi,
          distance: kmsDistance,
        };
      });
    }

    return {
      ...state,
      hotelDetail: {
        ...state.hotelDetail,
        isFetching: false,
        isFailed: false,
        id,
        name,
        image,
        address,
        description,
        checkinDetails: checkin_details,
        checkoutDetails: checkout_details,
        policies: { ...policies, childPolicies },
        fees,
        amenities: displayAmenities,
        allAmenities,
        starRating,
        guestRating,
        guestRatingCount,
        amenitiesRating,
        cleanlinessRating,
        comfortRating,
        conditionRating,
        locationRating,
        neighbourhoodRating,
        qualityRating,
        serviceRating,
        valueRating,
        guestRatingLabelId,
        topNotification,
        gallery: makeGalleryFromImages(images, name),
        galleryV2: makeGalleryV2FromImages(images),
        tripadvisor,
        location,
        pointsOfInterest,
        guest_rating,
      },
      defaultLocaleUsed: default_locale_used,
    };
  } catch (e) {
    console.log('[Discarded Hotel Content]', action.payload, e);
    return {
      ...state,
      hotelDetail: {
        isFetching: false,
        isFailed: true,
      },
    };
  }
}

const checkRecommendLevel = (tags = []) => {
  const isRecommended = tags.find(e => e.id === RECOMMEND_KEY);
  return !!isRecommended;
};

const checkStaffTravelLevel = (tags = []) => {
  const isStaffTravelRate = tags.find(e => e.id === STAFF_KEY);
  return !!isStaffTravelRate;
};

const reduceRooms = (rooms = [], checkout, serverTime) => {
  const result = [];

  rooms.forEach(room => {
    const { rates } = room;
    const validRates = reduceRates(
      rates,
      room.square_meter_area,
      checkout,
      serverTime
    );

    let isRecommendRoom = false;
    if (validRates.length === 1)
      isRecommendRoom = !!validRates[0].isRecommended;

    const roomWithValidRates = { ...room, rates: validRates };
    try {
      RoomSchema.validateSync(roomWithValidRates);

      const {
        id,
        name,
        gallery = {},
        room_amenities,
        views_from_room,
        occupancy,
        room_occupancy,
        bed_info,
        shared_bathroom,
      } = roomWithValidRates;

      let {
        square_meter_area: squareMeterArea,
        square_feet_area: squareFeetArea,
      } = roomWithValidRates;
      if (squareMeterArea) {
        squareMeterArea = roundOffPrice(squareMeterArea);
      }

      if (squareFeetArea) {
        squareFeetArea = roundOffPrice(squareFeetArea);
      }

      const { images } = gallery;

      const image = images ? getHeroImage(images, THUMBNAIL_SIZE) : null;

      result.push({
        id,
        name,
        image,
        occupancy,
        roomOccupancy: room_occupancy,
        otherAmenities:
          views_from_room &&
          views_from_room
            .filter(({ name }) => Boolean(name))
            .map(amenity => {
              return {
                ...amenity,
                displayId: `other#city_views`,
              };
            }),
        roomAmenities:
          room_amenities &&
          room_amenities
            .filter(({ name }) => Boolean(name))
            .map(amenity => {
              return {
                ...amenity,
                displayId: `room${AmenityIconsMap.split.level}${
                  AmenityIconsMap.room[amenity.id]
                }`,
                translationKey: getAmenityTranslationKey(
                  AMENITY_ICON_TYPE.ROOM,
                  amenity.id
                ),
              };
            }),
        squareMeterArea: squareMeterArea,
        rates: validRates,
        isRecommendRoom,
        galleryV2: images ? makeGalleryV2FromImages(images) : [],
        bedInfo: bed_info,
        squareFeetArea: squareFeetArea,
        sharedBathroom: shared_bathroom,
      });
    } catch (e) {
      console.log('[Discarded Room]', room, e);
    }
  });

  return result;
};

const reduceRates = (rates = [], squareMeterArea, checkout, serverTime) => {
  const result = [];

  let squareMeterAreaRoundOff = squareMeterArea;
  if (squareMeterAreaRoundOff) {
    squareMeterAreaRoundOff = roundOffPrice(squareMeterAreaRoundOff);
  }

  rates.forEach(rate => {
    try {
      RateSchema.validateSync(rate);

      const {
        id,
        rate_option_id,
        refundable,
        amenities,
        price,
        available_rooms: availableRooms,
        bed_groups: bedGroups,
        cancel_penalties = [],
        payable_at_site,
        big_life,
        pay_at_hotel_available,
        tags,
        cross_sell_rate,
        buy_now_pay_later_available,
        buy_now_pay_later_date,
        discount,
        duty_travel_rate,
        blocked_rate,
      } = rate;
      const { type: paymentDiscountType } = rate.discount || {};
      const isRecommended = checkRecommendLevel(tags);

      const isStaffTravel = checkStaffTravelLevel(tags);

      const hotelChargedPropertyFee =
        payable_at_site &&
        payable_at_site.find(item => item.type === 'mandatory_fee');

      const cancelPenalties = filterPenalties(
        cancel_penalties,
        checkout,
        serverTime
      );
      const displayedBedGroups = bedGroups.map(bedGroup => {
        return {
          ...bedGroup,
          squareMeterArea: squareMeterAreaRoundOff,
        };
      });
      const {
        average_nightly_price: averageNightlyPrice,
        total_tax: totalTax,
        total_fee: totalFee,
        tax_breakdown: taxBreakdown = [],
        fee_breakdown: feeBreakdown = [],
        total_big_points_redemption: totalBigpointsRedemption = {},
        total_payable_amount: totalPayable,
        discount_breakdown: discountBreakdown,
        total_discounted_price: totalDiscountedPrice,
        second_night: second_night,
      } = price;

      let {
        total_inclusive_price: totalInclusivePrice,
        total_exclusive_price: totalExclusivePrice,
      } = price;

      let {
        display_price: baseRate,
        strike_through: strikethroughPrice,
        price_after_deducting_big_points: baseRateAfterBigPointsDeduction,
      } = averageNightlyPrice;

      if (baseRate && baseRate.value) {
        baseRate.value = roundOffPrice(baseRate.value);
      }

      if (strikethroughPrice && strikethroughPrice.value) {
        strikethroughPrice.value = roundOffPrice(strikethroughPrice.value);
      }

      if (
        baseRateAfterBigPointsDeduction &&
        baseRateAfterBigPointsDeduction.value
      ) {
        baseRateAfterBigPointsDeduction.value = roundOffPrice(
          baseRateAfterBigPointsDeduction.value
        );
      }

      const totalTaxValue = Number(totalTax?.value);
      const hasTaxesAndFee = taxBreakdown.some(
        ({ type }) => type === CHARGE_TYPE.TAXES_AND_FEES
      );
      const totalPayableUsingBigpoints = totalBigpointsRedemption.amount;
      const currDate = new Date().getTime();
      let cancelPenalty = null;
      let isNonRefundable = false;
      if (!refundable) {
        cancel_penalties.forEach(i => {
          if (i.end && i.start) {
            const startDate = new Date(i.start).getTime();
            const endDate =
              i.start === i.end
                ? new Date(i.end).getTime() + 24 * 60 * 60 * 1000
                : new Date(i.end).getTime();
            if (startDate <= currDate && currDate <= endDate) {
              cancelPenalty = i;
            }
          } else if (i.end) {
            const endDate = new Date(i.end).getTime();
            if (currDate <= endDate) {
              cancelPenalty = i;
            }
          } else if (i.start) {
            const startDate = new Date(i.start).getTime();
            if (currDate >= startDate) {
              cancelPenalty = i;
            }
          }
        });

        if (cancelPenalty) {
          isNonRefundable =
            cancelPenalty.penalty_type === PENALTY_TYPE_FULL ? true : false;
        }
      }

      const { penalty_type: activePenalty } =
        cancelPenalties.find(({ start, end }) =>
          isActivePenalty({ start, end, serverTime })
        ) || {};
      const refundType = getRefundType(activePenalty);

      const hasFreeBreakfast =
        amenities &&
        amenities.find(
          amenity =>
            amenity.id === 2 ||
            amenity.id === 65 ||
            (amenity.id >= 22 && amenity.id <= 26) ||
            (amenity.id >= 70 && amenity.id <= 74)
        );
      const hasFreeLunch =
        amenities && amenities.find(amenity => amenity.id === 27);
      const hasFreeDinner =
        amenities &&
        amenities.find(amenity => amenity.id >= 28 && amenity.id <= 33);
      const hasHalfBoard =
        amenities &&
        amenities.find(amenity => amenity.id === 67 || amenity.id === 75);
      const hasFullBoard =
        amenities &&
        amenities.find(amenity => amenity.id === 58 || amenity.id === 69);

      let rateNameId = 0;
      if (hasFullBoard) rateNameId = 4;
      else if (hasHalfBoard) rateNameId = 3;
      else if (hasFreeBreakfast) {
        if (hasFreeLunch && hasFreeDinner) rateNameId = 4;
        else if (hasFreeLunch || hasFreeDinner) rateNameId = 3;
        else rateNameId = 2;
      } else if (hasFreeLunch) {
        if (hasFreeDinner) rateNameId = 3;
        else rateNameId = 5;
      } else if (hasFreeDinner) rateNameId = 6;
      else rateNameId = 1;

      result.push({
        amenities:
          amenities &&
          amenities
            .filter(({ name }) => Boolean(name))
            .map(amenity => {
              return {
                ...amenity,
                displayId: `rate${AmenityIconsMap.split.level}${
                  AmenityIconsMap.rate[amenity.id]
                }`,
                translationKey: getAmenityTranslationKey(
                  AMENITY_ICON_TYPE.RATE,
                  amenity.id
                ),
              };
            }),
        id,
        rateNameId,
        rateOptionId: rate_option_id,
        isFreeCancellation: refundable,
        isNonRefundable: isNonRefundable,
        baseRate,
        strikethroughPrice,
        totalInclusivePrice,
        totalExclusivePrice,
        totalPayableUsingBigpoints,
        totalPayable,
        totalDiscountedPrice,
        discountBreakdown,
        baseRateAfterBigPointsDeduction,
        isTaxIncluded: totalTaxValue === 0,
        isFeeIncluded: Number(totalFee?.value) === 0,
        isTaxAndFeeCombined: totalTaxValue > 0 && hasTaxesAndFee,
        feeBreakdown,
        taxBreakdown,
        payableAtSite: payable_at_site,
        availableRooms,
        bedGroups: displayedBedGroups.slice(0, 2),
        cancelPenalties,
        hotelChargedPropertyFee: hotelChargedPropertyFee
          ? Number(hotelChargedPropertyFee.amount.value)
          : 0,
        big_life,
        isPayAtHotel: pay_at_hotel_available,
        isRecommended,
        isStaffTravel,
        isCrossSellRate: cross_sell_rate,
        paymentDiscountType,
        refundType,
        isPayLater: buy_now_pay_later_available,
        payLaterDate: buy_now_pay_later_date,
        discountData: discount,
        second_night: second_night,
        isDutyTravelRate: duty_travel_rate,
        isBlockedRate: blocked_rate,
      });
    } catch (e) {
      console.log('[Discarded Rate]', rate, e);
    }
  });

  return result;
};

export function fetchHotelRoomsCompleted(state, action) {
  try {
    const {
      hotel_id,
      debug,
      rooms,
      serverTime,
      checkout,
      default_locale_used,
      big_membership,
    } = action.payload;
    const validRooms = reduceRooms(rooms || [], checkout, serverTime);
    const isEmptyHotelRoom = !rooms || rooms.length === 0;
    return {
      ...state,
      hotelDetail: {
        ...state.hotelDetail,
        id: hotel_id,
        debug: debug,
        rooms: validRooms,
        isEmptyHotelRoom,
      },
      defaultLocaleUsed: default_locale_used,
      bigMembership: big_membership,
    };
  } catch (e) {
    return {
      ...state,
      hotelDetail: {
        isFetching: false,
        isFailed: true,
      },
    };
  }
}

export function fetchHotelRoomsFailed(state, action) {
  const { type } = action.payload;
  if (type === RESOURCE_NOT_FOUND) {
    return {
      ...state,
      hotelDetail: {
        isFailed: true,
        isFetching: false,
        isResourceNotFound: true,
      },
    };
  }
  return {
    ...state,
    hotelDetail: {
      isFailed: true,
      isFetching: false,
    },
  };
}

export function clearHotelDetail(state) {
  return {
    ...state,
    hotelDetail: {
      isFetching: false,
      isFailed: false,
    },
  };
}

export const requestGuestCheckout = state => ({
  ...state,
  checkout: {
    paymentUrl: '',
    isFetching: true,
    isFailed: false,
    isRedirecting: false,
    priceChanged: false,
    hotelSoldOut: false,
    successFullOrder: false,
  },
});

export const checkoutHotelBookingCompleted = (state, action) => {
  const { data, status } = action.payload;
  const { payment_url = '' } = data;
  const checkout = {};
  checkout.paymentUrl = payment_url;
  checkout.isFetching = false;
  checkout.isFailed = false;
  checkout.isRedirecting = true;
  checkout.priceChanged = status === CHECKOUT_STATUS_CODE.PRICE_CHANGED;
  checkout.hotelSoldOut = status === CHECKOUT_STATUS_CODE.HOTEL_SOLD_OUT;
  checkout.successFullOrder = status === 200;
  checkout.bookingRequestId = data?.booking_request_id;
  return {
    ...state,
    checkout,
  };
};

export const clearHotelCheckoutUrl = state => {
  return {
    ...state,
    checkout: {
      paymentUrl: '',
      isFetching: false,
      isFailed: false,
      isRedirecting: false,
      priceChanged: false,
      hotelSoldOut: false,
      successFullOrder: false,
    },
  };
};

export function checkoutHotelBookingFailed(state) {
  return {
    ...state,
    checkout: {
      ...state.checkout,
      isFailed: true,
      isFetching: false,
      priceChanged: false,
      hotelSoldOut: false,
    },
  };
}

export function fetchSpecialRequestCompleted(state, action) {
  const { special_request } = action.payload;
  return {
    ...state,
    specialRequests: special_request,
  };
}

export function hotelHighlightFocus(state, action) {
  const { isHighLight, focusId, message = '', searchedHotel } = action.payload;
  const { hotelList } = state;
  const hotelListHighLight = hotelList.map(i => {
    if (isHighLight) {
      if (i.id === focusId) {
        i.focusHighLight = true;
        i.highlight = true;
        i.focusMessage = message;
        i.searchedHotel = searchedHotel;
      }
    } else if (i.focusHighLight) {
      i.highlight = false;
    }

    return i;
  });

  return {
    ...state,
    hotelList: hotelListHighLight,
  };
}

export function fetchPreCheckout(state) {
  return {
    ...state,
    preCheckout: {
      ...state.preCheckout,
      isFailed: false,
      isFetching: true,
    },
  };
}

export function fetchPreCheckoutCompleted(state, action) {
  const payload = action.payload || {};
  const { token, room_rate } = payload;

  if (room_rate) {
    // Handle Price Changed
    return {
      ...state,
      preCheckout: {
        ...state.preCheckout,
        isFetching: false,
        priceChanged: true,
        newRoomRate: room_rate,
      },
    };
  } else {
    // Handle successful pre-checkout
    const { notices = [] } = payload;

    let extraNote = '';
    notices.forEach(note => {
      const { title, subsections = [] } = note || {};
      const subNoteMsg = subsections
        .map(subNote => `${subNote.subtitle} ${subNote.description}`)
        .join('\n');
      extraNote += `${title}${subNoteMsg ? `\n${subNoteMsg}` : ''}`;
    });

    return {
      ...state,
      preCheckout: {
        ...state.preCheckout,
        ...initialState.preCheckout,
        isFailed: false,
        isFetching: false,
        extraNote,
        data: payload,
      },
    };
  }
}

export function fetchPreCheckoutFailed(state, action) {
  const payload = action.payload || {};
  const { type, message, field_errors, room_rate } = payload;

  if (room_rate) {
    // Handle Price Changed (409) - the response includes 'room_rate'
    return {
      ...state,
      preCheckout: {
        ...state.preCheckout,
        data: { ...payload },
        isFailed: false,
        isFetching: false,
        priceChanged: true,
        newRoomRate: room_rate,
      },
    };
  } else if (type) {
    // Error responses with a 'type' field
    switch (type) {
      case 'validation_error':
        // Handle Validation Errors (400)
        return {
          ...state,
          preCheckout: {
            ...state.preCheckout,
            isFailed: true,
            isFetching: false,
            error: message || 'Validation error occurred.',
            fieldErrors: field_errors || null,
          },
        };
      case 'sold_out':
        // Handle Selected Room Not Available (410)
        return {
          ...state,
          preCheckout: {
            data: { ...payload },
            isFailed: false,
            isFetching: false,
            roomUnavailable: true,
            errorMessage:
              message || 'The selected room is no longer available.',
          },
        };
      case 'Internal Server Error':
        // Handle Internal Server Error (500)
        return {
          ...state,
          preCheckout: {
            ...state.preCheckout,
            isFailed: true,
            isFetching: false,
            error: message || 'Internal server error occurred.',
          },
        };
      default:
        // Handle other error types
        return {
          ...state,
          preCheckout: {
            ...state.preCheckout,
            isFailed: true,
            isFetching: false,
            error: message || 'An error occurred.',
          },
        };
    }
  } else {
    // Handle other errors without 'type' field
    return {
      ...state,
      preCheckout: {
        ...state.preCheckout,
        isFailed: true,
        isFetching: false,
        error: message || 'An error occurred.',
      },
    };
  }
}

export function fetchGuestReview(state) {
  return {
    ...state,
    isFetching: true,
  };
}

export function fetchGuestReviewCompleted(state, action) {
  const reviews = action.payload || {};
  return {
    ...state,
    isFetching: false,
    guestReviews: {
      isFailed: false,
      isFetching: false,
      reviews,
    },
  };
}

export function fetchGuestReviewFailed(state, action) {
  return {
    ...state,
    guestReviews: {
      isFailed: true,
      isFetching: false,
    },
  };
}

export function updateHotelDetail(state, action) {
  const { hotelDetail } = action.payload;
  if (hotelDetail) {
    return {
      ...state,
      hotelDetail,
    };
  }
  return state;
}

export function fetchRefreshTokenSuccess(state, action) {
  const { refreshToken = '' } = action.payload || {};

  return {
    ...state,
    refreshToken: refreshToken,
  };
}

export default handleActions(
  {
    [types.FETCH_HOTEL_LIST_COMPLETED]: fetchHotelListCompleted,
    [types.FETCH_HOTEL_LIST]: fetchHotelList,
    [types.FETCH_HOTEL_LIST_V2]: fetchHotelListV2,
    [types.CLEAR_HOTEL_LIST]: clearHotelList,
    [types.FETCH_HOTEL_LIST_FAILED]: fetchHotelListFailed,
    [types.FETCH_HOTEL_DETAIL_FAILED]: fetchHotelDetailFailed,
    [types.FETCH_HOTEL_DETAIL]: fetchHotelDetail,
    [types.FETCH_HOTEL_DETAIL_COMPLETED]: fetchHotelDetailCompleted,
    [types.FETCH_HOTEL_ROOMS_COMPLETED]: fetchHotelRoomsCompleted,
    [types.FETCH_HOTEL_ROOMS_FAILED]: fetchHotelRoomsFailed,
    [types.FETCH_HOTEL_PRE_CHECKOUT]: fetchPreCheckout,
    [types.FETCH_HOTEL_PRE_CHECKOUT_COMPLETED]: fetchPreCheckoutCompleted,
    [types.FETCH_HOTEL_PRE_CHECKOUT_FAILED]: fetchPreCheckoutFailed,
    [types.CLEAR_HOTEL_DETAIL]: clearHotelDetail,
    [types.SORT_HOTEL_LIST]: sortHotelList,
    [types.FETCH_HOTEL_REFRESH_TOKEN_SUCCESS]: fetchRefreshTokenSuccess,
    [types.CHECKOUT_HOTEL_BOOKING]: requestGuestCheckout,
    [types.CHECKOUT_HOTEL_BOOKING_COMPLETED]: checkoutHotelBookingCompleted,
    [types.CHECKOUT_HOTEL_BOOKING_FAILED]: checkoutHotelBookingFailed,
    [types.CLEAR_HOTEL_CHECKOUT_URL]: clearHotelCheckoutUrl,
    [types.FETCH_SPECIAL_REQUEST_COMPLETED]: fetchSpecialRequestCompleted,
    [types.HOTEL_HIGHLIGHT_FOCUS]: hotelHighlightFocus,
    [types.UPDATE_HOTEL_DETAIL]: updateHotelDetail,
    [types.FETCH_HOTEL_GUEST_REVIEW]: fetchGuestReview,
    [types.FETCH_HOTEL_GUEST_REVIEW_COMPLETED]: fetchGuestReviewCompleted,
    [types.FETCH_HOTEL_GUEST_REVIEW_FAILED]: fetchGuestReviewFailed,
  },
  initialState
);
